import { z } from "zod";

/** Identité par défaut : celle que dessine `src/components/Logo.tsx`. */
const DEFAULT_NAME = "Encrelio";
const DEFAULT_LOGO_URL = "/logo.svg";

export const appConfig = {
  name: import.meta.env.VITE_APP_NAME || DEFAULT_NAME,
  tagline:
    import.meta.env.VITE_APP_TAGLINE || "Recouvrement de créances simplifié",
  /** White-label : vide = DA Encrelio (tokens.css). Voir src/lib/brand.ts. */
  primaryColor: import.meta.env.VITE_APP_PRIMARY_COLOR || undefined,
  accentColor: import.meta.env.VITE_APP_ACCENT_COLOR || undefined,
  logoUrl: import.meta.env.VITE_APP_LOGO_URL || DEFAULT_LOGO_URL,
  domain: import.meta.env.VITE_APP_DOMAIN || "encrelio.fr",
  supportEmail: import.meta.env.VITE_APP_SUPPORT_EMAIL || "support@encrelio.fr",
  /** Adresses publiques du site : contact, partenaires, données personnelles. */
  contactEmail: import.meta.env.VITE_APP_CONTACT_EMAIL || "hello@encrelio.fr",
  partnersEmail:
    import.meta.env.VITE_APP_PARTNERS_EMAIL || "partenaires@encrelio.fr",
  privacyEmail: import.meta.env.VITE_APP_PRIVACY_EMAIL || "dpo@encrelio.fr",
} as const;

/**
 * Marque blanche : ce que l'instance change par rapport à l'identité par
 * défaut. Un logo fourni remplace le logo dessiné ; un autre nom sans logo
 * donne un monogramme (le trombone d'Encrelio ne lui appartient pas).
 */
export const brandIdentity = {
  logoUrl: appConfig.logoUrl !== DEFAULT_LOGO_URL ? appConfig.logoUrl : null,
  customName: appConfig.name !== DEFAULT_NAME,
} as const;

const supabaseEnvSchema = z.object({
  VITE_SUPABASE_URL: z.string().url(),
  VITE_SUPABASE_PUBLISHABLE_KEY: z.string().startsWith("sb_publishable_"),
});

const parsed = supabaseEnvSchema.safeParse({
  VITE_SUPABASE_URL: import.meta.env.VITE_SUPABASE_URL,
  VITE_SUPABASE_PUBLISHABLE_KEY: import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY,
});

if (!parsed.success) {
  const issues = parsed.error.issues
    .map((i) => `  - ${i.path.join(".")}: ${i.message}`)
    .join("\n");
  throw new Error(
    `[encrelio] Configuration Supabase invalide :\n${issues}\n\n` +
      `Vérifie .env.staging / .env.production. Mode courant : ${import.meta.env.MODE}`,
  );
}

export const supabaseConfig = {
  url: parsed.data.VITE_SUPABASE_URL,
  publishableKey: parsed.data.VITE_SUPABASE_PUBLISHABLE_KEY,
} as const;

// Google OAuth — client_id seulement (public). Le client_secret est
// configuré comme Edge Function secret côté Supabase et ne transite jamais
// par le bundle. Si la variable est absente, la fonctionnalité Gmail est
// simplement désactivée côté UI.
export const googleOAuthConfig = {
  clientId: import.meta.env.VITE_GOOGLE_OAUTH_CLIENT_ID || "",
} as const;
